import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Motion";
import { HandHeart, Building2, Heart, X, Loader2 } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [10, 25, 50];

export default function Support({ onTalk }) {
  const { t } = useLang();
  const [donOpen, setDonOpen] = useState(false);
  const SUPPORTS = [
    { id: "benevole", icon: HandHeart, title: t("support.benevole.title"), text: t("support.benevole.text"), cta: t("support.benevole.cta"), color: "hsl(var(--coral))", bg: "#FFE5DE" },
    { id: "partenaire", icon: Building2, title: t("support.partner.title"), text: t("support.partner.text"), cta: t("support.partner.cta"), color: "hsl(var(--primary))", bg: "hsl(var(--sky))" },
    { id: "don", icon: Heart, title: t("support.don.title"), text: t("support.don.text"), cta: t("support.don.cta"), color: "hsl(var(--accent))", bg: "#FFF3C4" },
  ];

  return (
    <section id="soutenir" className="py-16 md:py-24 bg-background" data-testid="support-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <Reveal>
          <div className="max-w-2xl">
            <div className="chip">{t("support.chip")}</div>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-[1.05]">
              {t("support.title_1")} <span className="text-primary">{t("support.title_manner")}</span>
            </h2>
            <p className="mt-4 text-lg text-foreground/75 leading-relaxed">{t("support.subtitle")}</p>
          </div>
        </Reveal>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6" data-testid="support-grid">
          {SUPPORTS.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08}>
              <div className="rounded-3xl p-6 md:p-8 h-full flex flex-col bg-white border border-border hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(23,49,58,0.25)] transition-all">
                <div className="w-14 h-14 rounded-2xl grid place-items-center" style={{ backgroundColor: s.bg, color: s.color }}>
                  <s.icon className="w-7 h-7" strokeWidth={2.2} />
                </div>
                <h3 className="mt-5 font-heading text-xl leading-tight">{s.title}</h3>
                <p className="mt-2 text-base text-foreground/75 leading-relaxed flex-1">{s.text}</p>
                <button
                  onClick={() => (s.id === "don" ? setDonOpen(true) : onTalk())}
                  data-testid={`support-cta-${s.id}`}
                  className="mt-5 pill-btn self-start border-2 border-foreground bg-white text-foreground hover:bg-foreground hover:text-background"
                >
                  {s.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <DonateDialog open={donOpen} onClose={() => setDonOpen(false)} />
    </section>
  );
}

function DonateDialog({ open, onClose }) {
  const { t } = useLang();
  const [preset, setPreset] = useState(25);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const customAmount = parseInt(custom, 10);
  const useCustom = custom.trim() !== "" && !Number.isNaN(customAmount) && customAmount >= 1 && customAmount <= 100;

  const donate = async () => {
    setBusy(true);
    setError(false);
    try {
      const body = useCustom
        ? { lookup_key: "don_custom", quantity: customAmount, origin_url: window.location.origin }
        : { lookup_key: `don_${preset}`, quantity: 1, origin_url: window.location.origin };
      const { data } = await axios.post(`${API}/payments/checkout`, body);
      window.location.href = data.checkout_url;
    } catch {
      setError(true);
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[85] bg-foreground/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={() => !busy && onClose()}
          data-testid="donate-dialog"
        >
          <motion.div
            initial={{ y: 50, opacity: 0.8 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full md:max-w-md bg-background rounded-t-3xl md:rounded-3xl p-6 md:p-8"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-2xl leading-tight" data-testid="donate-title">{t("don.title")}</h3>
                <p className="mt-1 text-sm text-foreground/70">{t("don.subtitle")}</p>
              </div>
              <button onClick={onClose} disabled={busy} data-testid="donate-close" aria-label={t("common.close")} className="p-2 rounded-full border border-border hover:bg-muted">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {PRESETS.map((v) => (
                <button
                  key={v}
                  onClick={() => { setPreset(v); setCustom(""); }}
                  data-testid={`donate-preset-${v}`}
                  className={`rounded-2xl border-2 py-3.5 font-heading text-xl transition-colors ${
                    preset === v && !custom
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-white hover:border-primary/50"
                  }`}
                >
                  {v} €
                </button>
              ))}
            </div>

            <label className="mt-4 block">
              <span className="text-xs font-bold uppercase tracking-widest text-foreground/60">{t("don.custom_label")}</span>
              <div className="mt-1.5 flex items-center gap-2 rounded-2xl border-2 border-border bg-white px-4 py-3 focus-within:border-primary">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="1 – 100"
                  data-testid="donate-custom-input"
                  className="w-full bg-transparent outline-none font-heading text-xl"
                />
                <span className="font-heading text-xl text-foreground/50">€</span>
              </div>
            </label>

            {error && (
              <p className="mt-3 text-sm font-semibold text-red-600" data-testid="donate-error">{t("don.error")}</p>
            )}

            <button
              onClick={donate}
              disabled={busy || (custom.trim() !== "" && !useCustom)}
              data-testid="donate-submit"
              className="mt-6 pill-btn w-full justify-center bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? t("don.processing") : t("don.cta")}
            </button>
            <p className="mt-3 text-[11px] text-foreground/55 leading-snug text-center">{t("don.note")}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
